// Purpose: register a DID the carrier already routes to Console.
//
// A modal rather than a header form because registering a number is a
// deliberate, infrequent action with three related decisions. Putting it inline
// forced the screen header wider than the page and gave the fields no room.
//
// Uses the native <dialog> element so focus trapping, Escape to close, and the
// backdrop come from the platform rather than being reimplemented.

import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import type { ApiSipTrunk } from './apiClient';

export interface AddNumberValues {
  did: string;
  label: string;
  sipTrunkId: string;
}

export function AddNumberDialog(props: {
  open: boolean;
  trunks: ApiSipTrunk[];
  error: string | null;
  busy: boolean;
  onSubmit: (values: AddNumberValues) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [did, setDid] = useState('');
  const [label, setLabel] = useState('');
  const [sipTrunkId, setSipTrunkId] = useState('');

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (props.open && !dialog.open) {
      setDid('');
      setLabel('');
      setSipTrunkId('');
      dialog.showModal();
    }
    if (!props.open && dialog.open) dialog.close();
  }, [props.open]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    props.onSubmit({ did: did.trim(), label: label.trim(), sipTrunkId });
  }

  return (
    <dialog className="dialog" ref={ref} onCancel={props.onClose} onClose={props.onClose}>
      <form className="dialog-body" onSubmit={handleSubmit}>
        <header className="dialog-header">
          <h2>Add a number</h2>
          <p className="compact-copy">
            Register a DID your carrier already routes to Console. Console does not buy numbers.
          </p>
        </header>

        {props.error ? <p className="api-banner warning" role="alert">{props.error}</p> : null}

        <div className="field-stack">
          <label className="auth-field">
            <span>Phone number</span>
            <input
              value={did}
              onChange={(event) => setDid(event.target.value)}
              placeholder="+61390721700"
              autoFocus
              required
            />
          </label>
          <small className="compact-copy">Full international format, including the country code.</small>
        </div>

        <div className="field-stack">
          <label className="auth-field">
            <span>Name</span>
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Main reception"
              maxLength={80}
            />
          </label>
          <small className="compact-copy">Optional. Only for your reference; it never affects routing.</small>
        </div>

        <div className="field-stack">
          <label className="auth-field">
            <span>SIP trunk</span>
            <select value={sipTrunkId} onChange={(event) => setSipTrunkId(event.target.value)}>
              <option value="">Attach later</option>
              {props.trunks.map((trunk) => (
                <option key={trunk.sipTrunkId} value={trunk.sipTrunkId}>{trunk.name}</option>
              ))}
            </select>
          </label>
          <small className="compact-copy">
            The trunk that carries this number&rsquo;s calls. Console syncs it to LiveKit once attached.
          </small>
        </div>

        <footer className="dialog-actions">
          <button className="secondary-button" type="button" onClick={props.onClose}>Cancel</button>
          <button className="primary-button" type="submit" disabled={props.busy || !did.trim()}>
            {props.busy ? 'Adding…' : 'Add number'}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
